import React, { useRef, useState } from 'react'

// @mui material components
import { Alert, Icon, Link, Snackbar, Tooltip } from '@mui/material'
import Card from '@mui/material/Card'
import Grid from '@mui/material/Grid'
import axios from 'axios'
import Papa from 'papaparse'

import MDBox from 'components/MDBox'
import MDButton from 'components/MDButton'
import MDInput from 'components/MDInput'
import MDTypography from 'components/MDTypography'
import { useAuth } from 'context/auth'
import { useMaterialUIController } from 'context/materialUI'
import LoadingComponent from 'modules/Loading'

import { OrganisationMemberCSV } from '../../types'

function OrganisationMemberBulkImport(): JSX.Element {
	const [controller] = useMaterialUIController()
	const { user } = useAuth()
	const { configuration } = controller
	const [memberFile, setMemberFile] = useState<File | null>(null)
	const [uploadError, setUploadError] = useState<string | null>(null)
	const [uploadStatus, setUploadStatus] = useState<'loading' | 'error' | 'success' | null>(null)
	const [notifcationVisible, setNotificationVisible] = useState<boolean>(false)
	const [successQuantity, setSuccessQuantity] = useState<number>(0)
	const fileInputRef = useRef<HTMLInputElement | null>(null)
	const [fileInputKey, setFileInputKey] = useState<number>(0)

	const memberTypes = configuration?.organisation.memberTypes.map(m => m.name)

	const sendFileToBackend = async (file: File) => {
		if (!file) return

		setUploadStatus('loading')
		const formData = new FormData()
		formData.append('file', file)

		try {
			const response = await axios.post<{ message: string; quantity: number }>(
				`${process.env.REACT_APP_API_URL}/organisation-member/import`,
				formData,
				{
					headers: {
						Authorization: await user.auth.getIdToken(),
						'Content-Type': 'multipart/form-data',
					},
				}
			)
			setUploadStatus('success')
			setNotificationVisible(true)
			setSuccessQuantity(response.data.quantity)
		} catch (error: any) {
			console.error('Upload failed:', error.response?.data || error.message)
			setUploadStatus('error')
			setUploadError('Upload failed.')
		}
	}

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]
		if (fileInputRef.current) fileInputRef.current.value = ''
		if (!file) return

		if (file.type !== 'text/csv') {
			setUploadError('Only CSV files are supported.')
			setMemberFile(null)
			return
		}
		setFileInputKey(prev => prev + 1)

		setUploadError(null)

		const reader = new FileReader()
		reader.onload = async () => {
			const text = reader.result as string
			// CSV content validation
			const lines = text.trim().split('\n')
			const headers = lines[0].split(',').map(h => h.trim().toLowerCase())
			const requiredHeaders = [
				'id',
				'type',
				'name',
				'organisation',
				'start_date',
				'status',
				'phone_number',
				'email',
				'badge',
				'notes',
			]

			// Check for required headers
			const hasAllHeaders = requiredHeaders.every(header => headers.includes(header))
			if (!hasAllHeaders) {
				setUploadError(
					'CSV must contain the following headers: id, type, name, organisation, start_date, status, phone_number, email, badge, notes.'
				)
				setUploadStatus('error')
				return
			}

			// Validate each row
			const statusRegex = /^(ACTIVE|INACTIVE)$/
			const dateRegex = /^\d{2}\/\d{2}\/\d{4}$/

			Papa.parse(file, {
				header: true,
				skipEmptyLines: true,
				complete: function (results) {
					const rows = results.data

					for (let i = 0; i < rows.length; i++) {
						const row = rows[i] as OrganisationMemberCSV
						if (!row['id']) {
							setUploadError(`Id not valid for row ${i + 1}. Must be present.`)
							setUploadStatus('error')
						}

						if (!statusRegex.test(row['status'])) {
							setUploadError(`Invalid status in row ${i + 1}. Must be ACTIVE or INACTIVE.`)
							setUploadStatus('error')
							return
						}

						if (!dateRegex.test(row['start_date'])) {
							setUploadError(`Invalid date format in row ${i + 1}. Use dd-mm-yyyy format.`)
							setUploadStatus('error')
							return
						}

						if (!memberTypes.includes(row['type'])) {
							setUploadError(
								`Member Type not valid for row ${i + 1}. Must be one of the options: "${memberTypes}".`
							)
							setUploadStatus('error')
							return
						}

						if (!row['name']) {
							setUploadError(`Name not valid for row ${i + 1}. Must be present.`)
							setUploadStatus('error')
						}
					}
				},
			})
			setMemberFile(file)

			await sendFileToBackend(file)
		}
		reader.readAsText(file)
	}

	const handleClose = () => {
		setNotificationVisible(false)
	}

	return (
		<Card>
			<Snackbar
				open={notifcationVisible}
				autoHideDuration={8000}
				onClose={handleClose}
				anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
				sx={{ justifyContent: 'center', alignItems: 'center' }}
			>
				<Alert onClose={handleClose} severity={'info'} variant="filled" sx={{ width: '100%' }}>
					{`Successsfully Uploaded ${successQuantity} Members`}
				</Alert>
			</Snackbar>
			<MDBox p={2}>
				<Grid container spacing={3} alignItems="center">
					<Grid item xs={12}>
						<MDBox display={'flex'} flexDirection={'row'} justifyContent={'space-between'}>
							<MDBox display="flex" flexDirection="column" gap={2}>
								<MDTypography variant="h5" fontWeight="medium">
									Organisation Members Import
								</MDTypography>
								<MDTypography variant="button" fontWeight="regular" color="text">
									Upload CSV files to import organisation members in bulk. See the tooltip for the
									required format.
									<i>
										{' '}
										<Link
											href="/assets/import-templates/organisation-members-bulk-import.csv"
											download
											style={{ color: '#1a73e8', textDecoration: 'underline' }}
										>
											Click here to download an example.
										</Link>
									</i>
								</MDTypography>
							</MDBox>
							<Tooltip
								title={
									'CSV must include headers: id, type, name, organisation, start_date (dd/mm/yyyy), status (ACTIVE/INACTIVE), phone_number, email, badge, notes. Empty organisation uses default. One row per member.'
								}
								placement="bottom"
								arrow
							>
								<MDButton variant="outlined" color="secondary" size="small" circular iconOnly>
									<Icon>priority_high</Icon>
								</MDButton>
							</Tooltip>
						</MDBox>
					</Grid>

					<Grid item xs={12}>
						<MDBox
							display="flex"
							flexDirection="column"
							alignItems={'center'}
							justifyContent={'center'}
							gap={1}
						>
							<MDInput
								key={fileInputKey}
								ref={fileInputRef}
								accept=".csv"
								type="file"
								id="csv-upload-org-members"
								style={{ display: 'none' }}
								onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleFileUpload(e)}
							/>
							<label htmlFor="csv-upload-org-members">
								<MDButton component="span" color="info" variant="outlined">
									<Icon sx={{ mr: 1 }}>upload</Icon>
									Upload Organisation members CSV
								</MDButton>
							</label>
							<MDBox
								display={'flex'}
								flexDirection={'row'}
								justifyContent={'center'}
								alignItems={'center'}
								gap={2}
							>
								{memberFile && (
									<MDTypography variant="button" color="secondary">
										{memberFile.name}
									</MDTypography>
								)}
								{uploadStatus === 'loading' ? (
									<LoadingComponent type="circular_black" width={22} height={22} />
								) : uploadStatus === 'success' ? (
									<Icon color="success">check</Icon>
								) : uploadStatus === 'error' ? (
									<Icon color="error">close</Icon>
								) : (
									<></>
								)}
							</MDBox>
						</MDBox>
						{uploadError && (
							<MDTypography variant="caption" color="error">
								{uploadError}
							</MDTypography>
						)}
					</Grid>
				</Grid>
			</MDBox>
		</Card>
	)
}

export default OrganisationMemberBulkImport
